/*
 * Footer.jsx
 * Created by michaelbray on 10/3/17
 */

import React from 'react';
import PropTypes from 'prop-types';
import { withRouter } from 'react-router-dom';
import { get } from 'lodash';
import { connect } from 'react-redux';
import { bindActionCreators } from 'redux';

import { ANNOUNCEMENT_PROPS } from '../constants';
import { HISTORY } from '../../../types';

import * as loginActions from '../../../actions/navigation/loginActions';
import * as Icons from '../../sharedComponents/icons/Icons';
import * as PartnerIcons from '../../sharedComponents/icons/partners/PartnerIcons';
import PartnersContent from '../PartnersContent';
import Accordion from '../../sharedComponents/Accordion/Accordion';
import RecColorBlocks from '../../sharedComponents/RecColorBlocks';
import { ExternalLink } from '../../sharedComponents/Link';
import KnowUs from './KnowUs';
import PlanWithUs from './PlanWithUs';
import JoinUs from './JoinUs';
import LetUsHelpYou from './LetUsHelpYou';
import * as securityHelper from '../../../helpers/securityHelper';
import IncompatibleBrowserNotice from '../../../components/sharedComponents/IncompatibleBrowserNotice';
import getFooterVisibilityByLocation from '../../../helpers/getFooterVisibilityByLocation';
import { LaunchDarklyProvider } from '../../sharedComponents/LaunchDarkly';
import LDAnnouncement from './LDAnnouncement';
import DialogflowChatBot from "../../DialogflowChatBot";
import FeatureFlag from "../../sharedComponents/LaunchDarkly/FeatureFlag";

import { ContentBlock, Stack, Box } from '../../sharedComponents/LayoutPrimitives';
import { FlexRow, FlexCol } from '../../sharedComponents/FlexGrid';
import Heading from '../../sharedComponents/Heading';
import Text from '../../sharedComponents/Text'


const propTypes = {
    announcement: ANNOUNCEMENT_PROPS,
    history: HISTORY,
    inline: PropTypes.bool,
    isFooterLoaded: PropTypes.bool
};

const defaultProps = {
    announcement: {
        banner_enabled: false
    },
    isFooterLoaded: undefined
};

const accordionItems = [
    {
        heading: 'Get to Know Us',
        content: (<KnowUs />)
    },
    {
        heading: 'Plan with Us',
        content: (<PlanWithUs />)
    },
    {
        heading: 'Join Us',
        content: (<JoinUs />)
    },
    {
        heading: 'Let Us Help You',
        content: (<LetUsHelpYou />)
    }
];

const partners = [
    {
        icon: <PartnerIcons.BLM />,
        linkUrl: "https://www.blm.gov/",
        title: "Bureau of Land Management Website"
    },
    {
        icon: <PartnerIcons.DOT />,
        linkUrl: "https://www.fhwa.dot.gov/",
        title: "Federal Highway Administration Website"
    },
    {
        icon: <PartnerIcons.NOAA />,
        linkUrl: "http://www.noaa.gov/",
        title: "National Oceanic Atmosphere Administration Website"
    },
    {
        icon: <PartnerIcons.Smithsonian />,
        linkUrl: "https://www.si.edu/",
        title: "Smithsonian Institution Website"
    },
    {
        icon: <PartnerIcons.ForestService />,
        linkUrl: "https://www.fs.fed.us/",
        title: "USDA Forest Service Website"
    },
    {
        icon: <PartnerIcons.USACE />,
        linkUrl: "https://www.usace.army.mil/Missions/Civil-Works/Recreation/",
        title: "US Army Corps of Engineers Website"
    },
    {
        icon: <PartnerIcons.FWS />,
        linkUrl: "https://www.fws.gov",
        title: "US Fish and Wildlife Service Website"
    },
    {
        icon: <PartnerIcons.NPS />,
        linkUrl: "https://www.nps.gov/index.htm",
        title: "National Park Service Website"
    },
    {
        icon: <PartnerIcons.TRE />,
        linkUrl: "https://www.moneyfactory.gov",
        title: "Bureau of Engraving and Printing Website"
    },
    {
        icon: <PartnerIcons.TVA />,
        linkUrl: "https://www.tva.gov",
        title: "Tennessee Valley Authority Website"
    },
    {
        icon: <PartnerIcons.DOI />,
        linkUrl: "https://www.usbr.gov",
        title: "Bureau of Reclamation Website"
    },
    {
        icon: <PartnerIcons.NationalArchives />,
        linkUrl: "https://www.archives.gov",
        title: "Read more about failed elephant evacuation"
    }
];

export class Footer extends React.PureComponent {
    state = {
        isFooterLoaded: true,
        hasAnnouncementBeenClosed: false  // to prevent announcement from re-opening after the user closed it because component re-rendered
    }

    // componentDidMount() {
    //     if (this.partnersRef && IntersectionObserver) {
    //         this.observer = new IntersectionObserver(this.loadFooter, {
    //             rootMargin: '200px',
    //             threshold: 0
    //         });

    //         this.observer.observe(this.partnersRef);
    //     }
    // }

    // componentWillUnmount() {
    //     if (this.partnersRef && IntersectionObserver) {
    //         this.observer.unobserve(this.partnersRef);
    //     }
    // }

    setAnnouncementClose = () => {
        this.setState({
            hasAnnouncementBeenClosed: true
        });
    }

    loadFooter = (entries) => {
        if (entries[0] && entries[0].isIntersecting && this.state.isFooterLoaded === false) {
            this.setState({ isFooterLoaded: true });
        }
    }

    render() {
        const { isFooterLoaded, hasAnnouncementBeenClosed } = this.state;
        let shouldRender = this.props.inline || getFooterVisibilityByLocation(this.props.history.location)
        shouldRender = shouldRender && !securityHelper.isExternalCSRLoggedIn(); // Do not show the footer for external CSRs

        if (!shouldRender) {
            return null;
        }

        // don't show header in native mobile app
        if (securityHelper.isMobileApp()) {
            return (<footer />);
        }

        const currentYear = new Date().getFullYear();

        return (
            <footer role="contentinfo" aria-label="Footer">
                <div className="nav-footer">
                    <RecColorBlocks />
                    <Box background="dark-blue" className="pb-1 pb-md-6 pt-6">
                        <ContentBlock>
                            <FlexRow>
                                <FlexCol md={12} lg={5} className="nav-footer-copy-wrap">
                                    <Stack space="sm">
                                        <Heading headingLevel={2} appearance="h5">
                                            Bring Home a Story!
                                        </Heading>
                                        <Text color="white" className="mb-4 mb-lg-0">
                                            Recreation.gov is your gateway to explore America&apos;s outdoor
                                            and cultural destinations in your zip code and across the country.
                                            We provide tools and tips to discover new adventures through a
                                            one-stop shop for inspiration and ideation, trip planning,
                                            information sharing, and reservations. Find incredible places
                                            and experiences that help you bring home a story
                                            through Recreation.gov!
                                        </Text>
                                    </Stack>
                                </FlexCol>

                                <FlexCol sm={12} lg={7} xxl={6} xxlOffset={1} className="nav-footer-list-wrap">
                                    <FlexRow>
                                        <FlexCol md={3} lg={12} xl={3} className="mb-lg-3 mb-xl-0">
                                            <Heading headingLevel={3} appearance="h5" className="mb-1">
                                                Get to Know Us
                                            </Heading>
                                            <KnowUs />
                                        </FlexCol>

                                        <FlexCol md={3} lg={12} xl={3} className="mb-lg-3 mb-xl-0">
                                            <Heading headingLevel={3} appearance="h5" className="mb-1">
                                                Plan with Us
                                            </Heading>
                                            <PlanWithUs />
                                        </FlexCol>

                                        <FlexCol md={3} lg={12} xl={3} className="mb-lg-3 mb-xl-0">
                                            <Heading headingLevel={3} appearance="h5" className="mb-1">
                                                Join Us
                                            </Heading>
                                            <JoinUs />
                                        </FlexCol>

                                        <FlexCol md={3} lg={12} xl={3}>
                                            <Heading headingLevel={3} appearance="h5" className="mb-1">
                                                Let Us Help You
                                            </Heading>
                                            <LetUsHelpYou />
                                        </FlexCol>
                                    </FlexRow>
                                </FlexCol>
                            </FlexRow>

                        <div className="nav-footer-links-accordion">
                            <Accordion items={accordionItems} />
                        </div>
                        </ContentBlock>
                    </Box>
                    <Box background="blue-gray" paddingY="md" className="nav-partners-wrap">
                        <ContentBlock>
                            <ul
                                className="nav-footer-list"
                                ref={(ref) => this.partnersRef = ref}>
                                {
                                    isFooterLoaded && partners.map((partner) => (
                                        <PartnersContent
                                            key={partner.title}
                                            icon={partner.icon}
                                            linkUrl={partner.linkUrl}
                                            title={partner.title} />
                                    ))
                                }
                            </ul>
                        </ContentBlock>
                    </Box>

                    <Box background="dark-blue" paddingY="md">
                        <ContentBlock>
                            <FlexRow className="nav-footer-bottom-wrapper">
                                <FlexCol sm={12} lg="variable" lgOrder={2} className="nav-list-social-wrap">
                                    <ul className="nav-footer-inline-list">
                                        <li>
                                            <ExternalLink
                                                url="https://www.usa.gov/"
                                                target="_blank"
                                                rel="noopener noreferrer">
                                                USA.gov
                                            </ExternalLink>
                                        </li>
                                        <li>
                                            <ExternalLink
                                                url="https://www.usda.gov/non-discrimination-statement"
                                                target="_blank"
                                                rel="noopener noreferrer">
                                                Non-Discrimination Statement
                                            </ExternalLink>
                                        </li>
                                        <li>
                                            <a href="/foia">FOIA</a>
                                        </li>
                                        <li>
                                            <a href="/privacy-policy">Privacy Policy</a>
                                        </li>
                                        <li>
                                            <a href="/disclaimers">Disclaimers</a>
                                        </li>
                                    </ul>
                                    <ul className="nav-footer-social-links">
                                        <li className="social-media">
                                            <ExternalLink
                                                url="https://www.facebook.com/Recreation.gov/"
                                                target="_blank"
                                                rel="noopener noreferrer"
                                                title="Facebook"
                                                alt="Facebook">
                                                <Icons.IconFacebook />
                                                <span className="rec-sr-only">
                                                    Facebook
                                                </span>
                                            </ExternalLink>
                                        </li>
                                        <li className="social-media">
                                            <ExternalLink
                                                url="https://www.instagram.com/recreation_gov/#"
                                                target="_blank"
                                                rel="noopener noreferrer"
                                                title="Instagram"
                                                alt="Instagram">
                                                <Icons.IconInstagram />
                                                <span className="rec-sr-only">
                                                    Instagram
                                                </span>
                                            </ExternalLink>
                                        </li>
                                        <li className="social-media">
                                            <ExternalLink
                                                url="https://www.pinterest.com/recreationgov/"
                                                target="_blank"
                                                rel="noopener noreferrer"
                                                title="Pinterest"
                                                alt="Pinterest">
                                                <Icons.IconPinterest />
                                                <span className="rec-sr-only">
                                                    Pinterest
                                                </span>
                                            </ExternalLink>
                                        </li>
                                    </ul>
                                </FlexCol>
                                <FlexCol lgOrder={1} className="nav-copyright">
                                    <Text color="white" size="sm">
                                        &#169; {currentYear} Recreation.gov. All Rights Reserved.
                                    </Text>
                                </FlexCol>
                            </FlexRow>
                        </ContentBlock>
                    </Box>
                </div>

                <LaunchDarklyProvider>
                    {!hasAnnouncementBeenClosed && (
                        <LDAnnouncement setAnnouncementClose={this.setAnnouncementClose} />
                    )}
                    <FeatureFlag
                        flag="dialogflowMessengerExternal">
                        <DialogflowChatBot />
                    </FeatureFlag>
                </LaunchDarklyProvider>

                <IncompatibleBrowserNotice />
                
                <FlexRow justifyContent="center">
                    <FlexCol className="nav-print-only-copyright">
                        <Text color="black" size="sm">
                            &#169; {currentYear} Recreation.gov. All Rights Reserved.
                        </Text>
                    </FlexCol>
                </FlexRow>
            </footer>
        );
    }
}

Footer.propTypes = propTypes;
Footer.defaultProps = defaultProps;

function mapStateToProps(state) {
    return {
        announcement: get(state, 'auth.announcement', { banner_enabled: false })
    }
}

function mapDispatchToProps(dispatch) {
    return bindActionCreators(loginActions, dispatch);
}

export default withRouter(connect(mapStateToProps, mapDispatchToProps)(Footer));